import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import startCase from 'lodash.startcase';
import i18next from 'i18next';
import { useSelector } from 'react-redux';
import Status from '../RecentTransactions/Status';
import {
  getIsReadOnlyUser,
  getIsFullAccessUser,
  getIsPrimaryUser,
} from '../../../store/selectors/accountSelectors';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import useWindowSize from '../../../util/hooks/useIsMobileHook';
import Tooltip from '../../Tooltip/Tooltip';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { transactionTypesArray } from '../../../util/enum/api/transactionTypes';
import Button from '../../Button/Button';
import { TableArrowIcon } from '../../Table/TableRow';
import { ReactComponent as ChevronRight } from '../../../assets/images/svg/chevron-right.svg';

import { pendingTransactionCancel } from '../../Table/Components/PendingTransaction';
import { pxToRem } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import themeColors from '../../../assets/styles/themeColors';
import { uFlexColumn } from '../../../assets/styles/utility';
import useCheckPermissions from '../../../util/hooks/useCheckPermissions';

const TransactionTableTransaction = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const TransactionTableDate = styled.p`
  font-size: ${pxToRem(16)};
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.5;
  color: ${themeColors.colorTextSecondary};
  max-width: ${pxToRem(38)};
  width: 100%;
  text-align: center;
  margin-right: ${pxToRem(32)};
  text-transform: uppercase;
`;

const TransactionTableActions = styled.div`
  ${uFlexColumn};
  justify-content: center;
  min-width: 0;
`;

const TransactionTableMinusSign = styled.span`
  margin-left: ${pxToRem(32)};
`;

// Last grid cell: the cancel button, which also anchors the chevron on
// clickable rows. The chevron is taken out of the grid flow and hangs just past
// the cell's right edge, in the row's right padding, so it sits at the far
// right of the row without adding or resizing a grid column.
const TransactionTableLastCell = styled.div`
  position: relative;
  min-width: 0;
`;

const TransactionTableArrow = styled(TableArrowIcon)`
  position: absolute;
  top: 50%;
  left: 100%;
  margin-left: ${pxToRem(4)};
  transform: translateY(-50%);
`;

// As wide as the Cancel button and no wider: the row's click opens the order's
// details, so only the button itself may swallow it, not the rest of its cell.
const CancelButtonWrap = styled.div`
  width: max-content;
`;

const AmountCaption = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(18)};
  letter-spacing: 0;
  color: ${themeColors.colorTextSecondary};
`;

const TriggerOrderMediumText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(18)};
  letter-spacing: 0;
`;

const PendingTransactionRow = ({
  date,
  action,
  product,
  isSegregated,
  segregatedCaption,
  vaultName,
  label,
  amount,
  amountCaption,
  status,
  canCancel,
  handleModalOpen,
  isClickable,
  isTriggerOrder,
}) => {
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < 576;
  const transactionTypeConst = parseEnumType(transactionTypesArray, action);
  const { allowAccountChangesPermission } = useCheckPermissions();
  const isReadOnlyUser = useSelector(getIsReadOnlyUser);
  const isFullAccessUser = useSelector(getIsFullAccessUser);
  const isPrimaryUser = useSelector(getIsPrimaryUser);
  const isIneligibleToCancel =
    isReadOnlyUser || (isFullAccessUser && !isPrimaryUser);
  const transactionType = amount
    ? i18next.t(
        `portfolio.transactions.portfolioTypes.${transactionTypeConst}`,
        {
          amount: formatMoneyNumeral(amount),
        },
      )
    : i18next.t(
        `portfolio.transactions.portfolioTypesNonAmount.${transactionTypeConst}`,
      );

  return (
    <>
      <TransactionTableTransaction>
        {date && <TransactionTableDate>{date}</TransactionTableDate>}
        {(action || product || label) && (
          <TransactionTableActions>
            {label ? <p>{label}</p> : action && <p>{transactionType}</p>}
            {(product || vaultName) && (
              <p>
                {product}
                {product && vaultName && ' '}
                {vaultName}
                {isSegregated && ` (${segregatedCaption})`}
              </p>
            )}
          </TransactionTableActions>
        )}
      </TransactionTableTransaction>
      {amount !== undefined && (
        <div>
          <TriggerOrderMediumText>
            {amount === null || amount === 0 ? (
              <TransactionTableMinusSign>&#8722;</TransactionTableMinusSign>
            ) : (
              formatMoneyNumeral(amount)
            )}
          </TriggerOrderMediumText>
          {amountCaption && <AmountCaption>{amountCaption}</AmountCaption>}
        </div>
      )}
      {isMobile ? (
        <Tooltip text={startCase(status)}>
          <Status status={status} />
        </Tooltip>
      ) : (
        <Status status={status} />
      )}
      <TransactionTableLastCell>
        <CancelButtonWrap
          onClick={(event) => event.stopPropagation()}
          role="presentation"
        >
          <Button
            css={pendingTransactionCancel}
            onClick={handleModalOpen}
            disabled={
              !canCancel ||
              !allowAccountChangesPermission ||
              (isTriggerOrder && isIneligibleToCancel)
            }
            variant="primaryOutlined"
            size="sm"
          >
            {i18next.t('common.cancel')}
          </Button>
        </CancelButtonWrap>
        {isClickable && (
          <TransactionTableArrow role="presentation">
            <ChevronRight />
          </TransactionTableArrow>
        )}
      </TransactionTableLastCell>
    </>
  );
};

PendingTransactionRow.propTypes = {
  date: PropTypes.string,
  amount: PropTypes.number,
  amountCaption: PropTypes.string,
  status: PropTypes.string,
  action: PropTypes.number,
  product: PropTypes.string,
  isSegregated: PropTypes.bool,
  segregatedCaption: PropTypes.string,
  vaultName: PropTypes.string,
  label: PropTypes.string,
  handleModalOpen: PropTypes.func,
  canCancel: PropTypes.bool,
  // The row opens something when clicked (TableRow handles the click), so it
  // shows a chevron.
  isClickable: PropTypes.bool,
  isTriggerOrder: PropTypes.bool,
};

export default PendingTransactionRow;
